import { Component, computed, DestroyRef, HostBinding, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { getLorenzFormLayoutEnumString, LibFormValidityDotComponent, LorenzFormComponentDisplayOptions, LorenzFormComponentOptionsDefault, LorenzFormControl, LorenzFormGroup, LorenzFormLayoutEnum } from '@lib/forms';
import { LibSharedService } from '@lib/shared';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'lib-form-control-select',
	imports: [CommonModule, FormsModule, ReactiveFormsModule, LibFormValidityDotComponent, TranslateModule],
	templateUrl: './lib-form-control-select.component.html',
	styleUrl: './lib-form-control-select.component.scss'
})
export class LibFormControlSelectComponent implements OnInit {
	public readonly destroyRef = inject(DestroyRef);
	public readonly libSharedService = inject(LibSharedService);

	@Input({ required: true, alias: 'control' }) lorenzFormControl!: LorenzFormControl;

	@Input({ required: false, alias: 'options' }) lorenzFormComponentDisplayOptions: LorenzFormComponentDisplayOptions = {};

	@HostBinding('attr.class') hostCssClass: string | null = null;

	lorenzFormGroup: LorenzFormGroup | null = null;

	private previousValue: string | null = null;

	lorenzFormControlOptions = computed(() => {
		const lang = this.libSharedService.getCurrentLanguage();
		// console.log('this.libSharedService.currentLanguage', lang);
		const localizedOptions = this.lorenzFormControl.getOptions(lang) || [];
		return localizedOptions;
	});

	lorenzFormControlValue = computed(() => {
		const lang = this.libSharedService.getCurrentLanguage();
		// console.log('this.libSharedService.currentLanguage', lang);
		const lorenzFormControlOptions = this.lorenzFormControl.getOptions('de') || [];
		const lorenzFormControlValue = lorenzFormControlOptions.find((o) => o.value === this.lorenzFormControl.value)?.label ?? '';
		return lorenzFormControlValue;
	});

	ngOnInit() {
		const destroyed = new Subject<void>();

		this.destroyRef.onDestroy(() => {
			destroyed.next();
			destroyed.complete();
		});

		this.lorenzFormGroup = this.lorenzFormControl.parent as LorenzFormGroup;

		this.lorenzFormControl.payload.displayOptions = this.lorenzFormComponentDisplayOptions;

		// prettier-ignore
		this.hostCssClass = [
				this.lorenzFormControl.getKey(),
				getLorenzFormLayoutEnumString(this.lorenzFormComponentDisplayOptions?.layout ?? null)
			].join(' ');

		this.previousValue = this.lorenzFormControl.value;

		// prettier-ignore
		this.lorenzFormControl.valueChanges
				.pipe(takeUntil(destroyed))
				.subscribe((newValue: string | null) => {
					this.setValue(newValue);
				});

		if (!this.lorenzFormControl.value) {
			this.lorenzFormControl.setValue(this.lorenzFormControl.getDefaultOption());
		} else {
			this.lorenzFormControl.setValue(this.lorenzFormControl.value);
		}
	}

	get isValueSelected(): boolean {
		return !!this.lorenzFormControl?.value;
	}

	get hasLayoutFloatingLabels(): boolean {
		return this.lorenzFormComponentDisplayOptions?.layout === LorenzFormLayoutEnum.FLOATING_LABELS;
	}

	get hasPlaceholder(): boolean {
		return !!(this.lorenzFormControl.getPlaceholder() ?? false);
	}

	get placeholder(): string {
		if (this.hasLayoutFloatingLabels) {
			return ' ';
		}
		return this.lorenzFormControl.getPlaceholder();
	}

	get showErrorMessages(): boolean {
		return this.lorenzFormControl.showErrors() && (this.lorenzFormComponentDisplayOptions.showErrorMessages ?? LorenzFormComponentOptionsDefault.showErrorMessages);
	}

	private setValue(newValue: string | null): void {
		if (!!this.lorenzFormControl?.payload?.allowOnlyOptions) {
			const allowedValues = this.lorenzFormControl.getOptions().map((option) => option.value);
			if (!allowedValues.includes(this.lorenzFormControl.value)) {
				newValue = null;
				this.lorenzFormControl.setValue(newValue, { emitEvent: false });
			}
		}

		this.lorenzFormControl.setPayloadProperty('previousValue', this.previousValue);
		this.previousValue = newValue;

		this.lorenzFormControl.getRoot()?.updateValueAndValidity();
	}
}
